/**
 * Renderer della card condivisibile — Canvas 2D nativo, 1080×1920 (Story).
 * Nessuna dipendenza dal DOM del popup: riceve un canvas e i dati, disegna.
 * Icone proprietarie come Path2D (mai emoji: resa identica su ogni device).
 * Il PNG prodotto dal canvas non contiene EXIF/geotag per costruzione.
 */

export const CARD_W = 1080;
export const CARD_H = 1920;

// Safe area Story: Instagram sovrappone avatar in alto e CTA in basso
const SIDE = 84;
const TOP = 150;
const BOTTOM = 130;
const CONTENT_W = CARD_W - SIDE * 2;

const FONT_STACK = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
const WHITE = '#f6f4fb';
const ORANGE = '#fb923c';
const ORANGE_DEEP = '#f97316';

// ── Icone proprietarie (path SVG 24×24 → Path2D) ──
const ICONS = {
  flame: {
    fills: [
      { d: 'M12 2.5c.8 3.4-2.6 4.6-2.6 8a4.6 4.6 0 0 0 9.2 0c0-1.7-.8-3-1.7-4.3-.4 1.7-1.9 2.3-1.9.9 0-1.8-1-3.6-3-4.6z', color: 'currentColor' },
      { d: 'M10.6 13.6a2.5 2.5 0 0 0 5 0c0-.9-.5-1.6-1-2.3-.2.9-1 1.2-1 .5 0-.9-.5-1.9-1.5-2.4.4 1.8-1.5 2.4-1.5 4.2z', color: 'rgba(0,0,0,.28)' },
    ],
  },
  medal: {
    fills: [
      { d: 'M12 4.8a5.2 5.2 0 1 1 0 10.4 5.2 5.2 0 0 1 0-10.4z', color: 'currentColor' },
      { d: 'M12 7.6a2.4 2.4 0 1 1 0 4.8 2.4 2.4 0 0 1 0-4.8z', color: 'rgba(0,0,0,.30)' },
      { d: 'M9 14.5 7.2 20l3-1.4L12 21l1.8-2.4 3 1.4-1.8-5.5z', color: 'currentColor', alpha: 0.75 },
    ],
  },
  level: {
    strokes: [
      { d: 'M6 13.5 12 8l6 5.5M6 18.5 12 13l6 5.5', width: 2.4 },
    ],
  },
};

const drawIcon = (ctx, name, x, y, size, color) => {
  const icon = ICONS[name];
  if (!icon) return;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 24, size / 24);
  for (const f of icon.fills || []) {
    ctx.globalAlpha = f.alpha ?? 1;
    ctx.fillStyle = f.color === 'currentColor' ? color : f.color;
    ctx.fill(new Path2D(f.d));
  }
  for (const s of icon.strokes || []) {
    ctx.strokeStyle = color;
    ctx.lineWidth = s.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke(new Path2D(s.d));
  }
  ctx.restore();
};

// ── Testo con tracking (letterSpacing nativo se c'è, manuale altrimenti) ──
const setFont = (ctx, weight, size) => {
  ctx.font = `${weight} ${size}px ${FONT_STACK}`;
};

const textWidth = (ctx, text, tracking = 0) => {
  if (!tracking) return ctx.measureText(text).width;
  let w = 0;
  for (const ch of text) w += ctx.measureText(ch).width + tracking;
  return Math.max(0, w - tracking);
};

const drawText = (ctx, text, x, y, { weight = 400, size = 30, color = WHITE, tracking = 0, align = 'left' } = {}) => {
  setFont(ctx, weight, size);
  ctx.fillStyle = color;
  ctx.textBaseline = 'alphabetic';
  if (!tracking) {
    ctx.textAlign = align;
    ctx.fillText(text, x, y);
    ctx.textAlign = 'left';
    return;
  }
  const total = textWidth(ctx, text, tracking);
  let cx = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
  ctx.textAlign = 'left';
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + tracking;
  }
};

/** Riduce la dimensione finché il testo entra in maxWidth (min 40%). */
const fitSize = (ctx, text, weight, startSize, maxWidth, tracking = 0) => {
  let size = startSize;
  const min = Math.max(24, startSize * 0.4);
  setFont(ctx, weight, size);
  while (size > min && textWidth(ctx, text, tracking * (size / startSize)) > maxWidth) {
    size -= Math.max(2, size * 0.04);
    setFont(ctx, weight, size);
  }
  return size;
};

/** Tronca con ellissi entro maxWidth (per nomi workout molto lunghi). */
const truncate = (ctx, text, weight, size, maxWidth, tracking = 0) => {
  setFont(ctx, weight, size);
  if (textWidth(ctx, text, tracking) <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && textWidth(ctx, `${t}…`, tracking) > maxWidth) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
};

// ── Sfondi ──
const paintClassicBackground = (ctx) => {
  // gradiente ~168°: dal blu-indaco notte in alto-sx al viola in basso-dx
  const g = ctx.createLinearGradient(0, 0, CARD_W * 0.35, CARD_H);
  g.addColorStop(0, '#262157');
  g.addColorStop(0.5, '#40307d');
  g.addColorStop(1, '#653587');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // luce radiale alta a destra
  const light = ctx.createRadialGradient(CARD_W * 0.82, CARD_H * 0.12, 0, CARD_W * 0.82, CARD_H * 0.12, 560);
  light.addColorStop(0, 'rgba(139,130,255,.30)');
  light.addColorStop(1, 'rgba(139,130,255,0)');
  ctx.fillStyle = light;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  // angolo profondo in basso a sinistra
  const dark = ctx.createRadialGradient(CARD_W * 0.08, CARD_H * 0.96, 0, CARD_W * 0.08, CARD_H * 0.96, 700);
  dark.addColorStop(0, 'rgba(10,6,32,.55)');
  dark.addColorStop(1, 'rgba(10,6,32,0)');
  ctx.fillStyle = dark;
  ctx.fillRect(0, 0, CARD_W, CARD_H);

  paintKnurl(ctx, 0.06);
};

// zigrino: righe diagonali a 115°, quasi invisibili
const paintKnurl = (ctx, alpha) => {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.translate(CARD_W / 2, CARD_H / 2);
  ctx.rotate((115 * Math.PI) / 180);
  const L = Math.hypot(CARD_W, CARD_H);
  for (let x = -L; x <= L; x += 46) {
    ctx.beginPath();
    ctx.moveTo(x, -L);
    ctx.lineTo(x, L);
    ctx.stroke();
  }
  ctx.restore();
};

/** Foto cover-fit 9:16 con trasformazione utente (drag/zoom), clampata. */
export const coverTransform = (photoW, photoH, userScale = 1, userDx = 0, userDy = 0) => {
  const base = Math.max(CARD_W / photoW, CARD_H / photoH);
  const scale = base * Math.min(Math.max(userScale, 1), 4);
  const w = photoW * scale;
  const h = photoH * scale;
  const maxDx = (w - CARD_W) / 2;
  const maxDy = (h - CARD_H) / 2;
  const dx = Math.min(Math.max(userDx, -maxDx), maxDx);
  const dy = Math.min(Math.max(userDy, -maxDy), maxDy);
  return { x: (CARD_W - w) / 2 + dx, y: (CARD_H - h) / 2 + dy, w, h, dx, dy };
};

const paintPhotoBackground = (ctx, photo, photoPosition, dataPlacement) => {
  const { x, y, w, h } = coverTransform(
    photo.width, photo.height,
    photoPosition?.scale ?? 1, photoPosition?.dx ?? 0, photoPosition?.dy ?? 0
  );
  ctx.drawImage(photo, x, y, w, h);

  // scrim a doppio gradiente: denso dal lato dei dati, leggero dall'altro
  const g = ctx.createLinearGradient(0, 0, 0, CARD_H);
  if (dataPlacement === 'top') {
    g.addColorStop(0, 'rgba(12,8,28,.88)');
    g.addColorStop(0.38, 'rgba(12,8,28,.34)');
    g.addColorStop(0.56, 'rgba(12,8,28,0)');
    g.addColorStop(0.74, 'rgba(12,8,28,.08)');
    g.addColorStop(1, 'rgba(12,8,28,.55)');
  } else {
    g.addColorStop(0, 'rgba(12,8,28,.55)');
    g.addColorStop(0.26, 'rgba(12,8,28,.08)');
    g.addColorStop(0.44, 'rgba(12,8,28,0)');
    g.addColorStop(0.62, 'rgba(12,8,28,.34)');
    g.addColorStop(1, 'rgba(12,8,28,.88)');
  }
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, CARD_W, CARD_H);
};

// ── Elementi ──
const drawHeader = (ctx, dateLabel) => {
  drawText(ctx, 'UPTRA', SIDE, TOP + 36, { weight: 800, size: 40, tracking: 12, color: WHITE });
  const w = textWidth(ctx, 'UPTRA', 12);
  drawText(ctx, '.', SIDE + w + 2, TOP + 36, { weight: 800, size: 40, color: ORANGE });

  // pill data
  const label = (dateLabel || '').toUpperCase();
  setFont(ctx, 600, 27);
  const tw = textWidth(ctx, label, 3.2);
  const pw = tw + 56;
  const px = CARD_W - SIDE - pw;
  const py = TOP - 6;
  ctx.strokeStyle = 'rgba(246,244,251,.28)';
  ctx.lineWidth = 2;
  roundRect(ctx, px, py, pw, 62, 31);
  ctx.stroke();
  drawText(ctx, label, px + 28, py + 42, { weight: 600, size: 27, tracking: 3.2, color: 'rgba(246,244,251,.75)' });
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawBarbell = (ctx, y) => {
  const plate = (x, h) => {
    ctx.fillStyle = ORANGE;
    roundRect(ctx, x, y - h / 2, 15, h, 6);
    ctx.fill();
  };
  ctx.fillStyle = 'rgba(246,244,251,.35)';
  ctx.fillRect(SIDE + 74, y - 2, CONTENT_W - 148, 4);
  plate(SIDE, 30);
  plate(SIDE + 24, 44);
  plate(CARD_W - SIDE - 15, 30);
  plate(CARD_W - SIDE - 39, 44);
};

/** Eroe: alone + valore gigante (auto-fit) + etichetta. Ritorna la y finale. */
const drawHero = (ctx, hero, topY, { compact = false } = {}) => {
  const numeric = hero.type !== 'badge';
  const startSize = compact ? 200 : numeric ? 250 : 150;

  // alone dietro il numero
  const halo = ctx.createRadialGradient(SIDE + 320, topY + 90, 0, SIDE + 320, topY + 90, 480);
  halo.addColorStop(0, 'rgba(251,146,60,.24)');
  halo.addColorStop(1, 'rgba(251,146,60,0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, topY - 160, CARD_W, 520);

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,.35)';
  ctx.shadowBlur = 48;
  ctx.shadowOffsetY = 8;

  const unit = hero.unit || '';
  setFont(ctx, 900, startSize);
  let size = startSize;
  const unitSize = () => Math.round(size * 0.38);
  const totalW = () => {
    setFont(ctx, 900, size);
    let w = textWidth(ctx, hero.value, -size * 0.03);
    if (unit) {
      setFont(ctx, 800, unitSize());
      w += 18 + ctx.measureText(unit).width;
    }
    return w;
  };
  while (size > startSize * 0.4 && totalW() > CONTENT_W) size -= Math.max(3, size * 0.05);

  const baseY = topY + size * 0.86;
  setFont(ctx, 900, size);
  drawText(ctx, hero.value, SIDE, baseY, { weight: 900, size, color: '#ffffff', tracking: -size * 0.03 });
  const vw = textWidth(ctx, hero.value, -size * 0.03);
  if (unit) {
    drawText(ctx, unit, SIDE + vw + 18, baseY, { weight: 800, size: unitSize(), color: ORANGE });
  }
  ctx.restore();

  let y = baseY + 44;
  drawText(ctx, hero.label, SIDE, y + 20, { weight: 700, size: 34, tracking: 8, color: ORANGE });
  y += 54;
  if (hero.sublabel) {
    const sub = truncate(ctx, hero.sublabel, 600, 32, CONTENT_W);
    drawText(ctx, sub, SIDE, y + 30, { weight: 600, size: 32, color: 'rgba(246,244,251,.75)' });
    y += 48;
  }
  return y;
};

const drawStats = (ctx, stats, y) => {
  if (!stats.length) return y;
  const slot = CONTENT_W / stats.length;
  stats.forEach((st, i) => {
    const x = SIDE + slot * i;
    let vx = x;
    if (st.icon) {
      drawIcon(ctx, st.icon, x, y - 40, 48, ORANGE);
      vx += 58;
    }
    drawText(ctx, st.value, vx, y, { weight: 800, size: 64, color: WHITE, tracking: -1 });
    if (st.unit) {
      setFont(ctx, 800, 64);
      const w = textWidth(ctx, st.value, -1);
      drawText(ctx, st.unit, vx + w + 8, y, { weight: 700, size: 32, color: WHITE });
    }
    drawText(ctx, st.label, x, y + 42, { weight: 600, size: 26, tracking: 5, color: 'rgba(246,244,251,.62)' });
  });
  return y + 42;
};

const drawMoment = (ctx, moment, y) => {
  const h = 88;
  ctx.fillStyle = 'rgba(251,146,60,.13)';
  roundRect(ctx, SIDE, y, CONTENT_W, h, 30);
  ctx.fill();
  ctx.strokeStyle = 'rgba(251,146,60,.40)';
  ctx.lineWidth = 3;
  roundRect(ctx, SIDE, y, CONTENT_W, h, 30);
  ctx.stroke();

  drawIcon(ctx, moment.icon || 'medal', SIDE + 30, y + 20, 50, ORANGE);
  setFont(ctx, 800, 34);
  const titleW = ctx.measureText(moment.title).width;
  drawText(ctx, moment.title, SIDE + 100, y + 56, { weight: 800, size: 34, color: WHITE });
  const sub = truncate(ctx, ` · ${moment.subtitle || ''}`, 500, 30, CONTENT_W - 130 - titleW);
  drawText(ctx, sub, SIDE + 100 + titleW, y + 56, { weight: 500, size: 30, color: 'rgba(246,244,251,.72)' });
  return y + h;
};

const drawCorners = (ctx, corner, y) => {
  if (corner.streak) {
    drawIcon(ctx, 'flame', SIDE, y - 46, 52, ORANGE);
    drawText(ctx, `${corner.streak} giorni`, SIDE + 62, y, { weight: 800, size: 44, color: WHITE });
    drawText(ctx, 'DI FILA', SIDE + 62, y + 34, { weight: 600, size: 26, tracking: 4.5, color: 'rgba(246,244,251,.62)' });
  }
  if (corner.xp) {
    drawText(ctx, `+${corner.xp.gained} XP`, CARD_W - SIDE, y, { weight: 700, size: 36, color: ORANGE, align: 'right' });
    setFont(ctx, 600, 26);
    const lvl = `LIVELLO ${corner.xp.level}`;
    const lw = textWidth(ctx, lvl, 4.5);
    drawText(ctx, lvl, CARD_W - SIDE - lw, y + 34, { weight: 600, size: 26, tracking: 4.5, color: 'rgba(246,244,251,.62)' });
    drawIcon(ctx, 'level', CARD_W - SIDE - lw - 34, y + 10, 28, ORANGE);
  }
};

const drawFooter = (ctx) => {
  drawText(ctx, 'UPTRA.IT', CARD_W / 2, CARD_H - BOTTOM + 10, {
    weight: 700, size: 30, tracking: 8.5, color: 'rgba(246,244,251,.45)', align: 'center',
  });
};

/**
 * Disegna la card completa. `photo` (ImageBitmap/canvas/img) opzionale;
 * `dataPlacement`: 'top' | 'bottom' (solo variante Foto);
 * `photoPosition`: { scale, dx, dy } dalla UI di inquadratura.
 */
export const drawShareCard = (canvas, {
  heroMetric,
  workoutName = '',
  date = new Date(),
  photo = null,
  photoPosition = null,
  dataPlacement = 'bottom',
} = {}) => {
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, CARD_W, CARD_H);

  const { hero, stats, moment, corner } = heroMetric;

  if (photo) {
    paintPhotoBackground(ctx, photo, photoPosition, dataPlacement);
  } else {
    paintClassicBackground(ctx);
  }

  const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  const d = date instanceof Date ? date : new Date(date);
  drawHeader(ctx, `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`);

  const name = truncate(ctx, (workoutName || '').toUpperCase(), 600, 38, CONTENT_W, 7);

  if (photo) {
    // ── VARIANTE FOTO: blocco dati compatto in alto o in basso ──
    const blockH = 560;
    let y = dataPlacement === 'top' ? TOP + 96 : CARD_H - BOTTOM - 60 - blockH;
    drawText(ctx, name, SIDE, y + 28, { weight: 600, size: 38, tracking: 7, color: 'rgba(246,244,251,.72)' });
    y += 50;
    y = drawHero(ctx, hero, y, { compact: true });
    y += 40;
    drawBarbell(ctx, y);
    y += 78;
    // nella Foto la streak entra nella riga dati (max 4 slot), niente angoli
    const photoStats = [...stats];
    if (corner.streak && photoStats.length < 4 && !photoStats.some((s) => s.key === 'streak')) {
      photoStats.push({ key: 'streak', value: String(corner.streak), unit: '', label: 'STREAK', icon: 'flame' });
    }
    drawStats(ctx, photoStats, y + 40);
  } else {
    // ── VARIANTE CLASSIC ──
    let y = TOP + 130;
    drawText(ctx, name, SIDE, y + 28, { weight: 600, size: 38, tracking: 7, color: 'rgba(246,244,251,.72)' });
    y += 66;
    y = drawHero(ctx, hero, y);
    y += 56;
    drawBarbell(ctx, y);
    y += 84;
    y = drawStats(ctx, stats, y + 40);
    if (moment) {
      drawMoment(ctx, moment, y + 60);
    }
    drawCorners(ctx, corner, CARD_H - BOTTOM - 120);
  }

  drawFooter(ctx);
  return canvas;
};
